import { useQuery } from "@apollo/client";
import { Flex, FlexItem, Loading, P, Popover } from "orcs-design-system";
import PropTypes from "prop-types";
import { useCallback, useState } from "react";
import { getGroupBaseQuery } from "src/queries/group.graphql";
import PersistedMoveIndicator from "./PersistedMoveIndicator";
import PreviousLocationBreadcrumb from "./PreviousLocationBreadcrumb";

const ACCESSIBLE_LABEL =
  "This team was moved in TeamForm; placement is saved and may differ from the source hierarchy.";

const PersistentMoveExplanation = () => (
  <P>
    This is a persistent move, meaning that this team and its child teams have
    been moved within TeamForm and may not match source system hierarchy. This
    team will continue to live in this location until it is manually moved
    again.
  </P>
);

const PersistedMoveLoadingTooltipBody = () => (
  <Flex alignItems="center" gap="s">
    <Loading ariaLabel="Loading previous location" />
    <P mb="0">Loading previous location…</P>
  </Flex>
);

const PersistedMoveMissingPreviousLocationTooltipBody = () => (
  <Flex flexDirection="column" gap="s">
    <P mb="0">
      This team has been moved, but the previous location is no longer
      available.
    </P>
    <PersistentMoveExplanation />
  </Flex>
);

const PersistedMoveTooltipBody = ({ parentGroup }) => (
  <Flex flexDirection="column" gap="s">
    <P mb="0">This team has been moved from:</P>
    <PreviousLocationBreadcrumb group={parentGroup} />
    <PersistentMoveExplanation />
  </Flex>
);

PersistedMoveTooltipBody.propTypes = {
  parentGroup: PropTypes.object.isRequired,
};

const PersistedMoveParentInfo = ({ group, testId }) => {
  const { propertyOverrides } = group;

  const { directParent } = propertyOverrides || {};
  const previousDirectParentId = directParent?.previousDirectParentId;

  // Lazy fetch: defer the previous-parent query until the user actually
  // interacts with the indicator (hover / focus / tap). Avoids firing one
  // query per moved team when a page first renders.
  const [hasInteracted, setHasInteracted] = useState(false);
  const triggerLoad = useCallback(() => setHasInteracted(true), []);

  const { data, loading } = useQuery(getGroupBaseQuery, {
    variables: { groupId: previousDirectParentId },
    skip: !previousDirectParentId || !hasInteracted,
  });

  if (!previousDirectParentId) {
    return null;
  }

  const parentGroup = data?.group;
  // Three distinct tooltip states:
  // 1. Loading — query has not been triggered yet or is in flight.
  // 2. Missing — query completed but returned no group (e.g. the previous
  //    parent has been deleted). We must NOT keep showing the spinner.
  // 3. Loaded — query completed and we have the previous-parent group.
  const isLoadingTooltip = !hasInteracted || loading;
  const isPreviousLocationMissing = !isLoadingTooltip && !parentGroup;

  let tooltip;
  if (isLoadingTooltip) {
    tooltip = <PersistedMoveLoadingTooltipBody />;
  } else if (isPreviousLocationMissing) {
    tooltip = <PersistedMoveMissingPreviousLocationTooltipBody />;
  } else {
    tooltip = <PersistedMoveTooltipBody parentGroup={parentGroup} />;
  }

  return (
    <FlexItem
      flex="0 0 auto"
      alignSelf="center"
      role="button"
      tabIndex={0}
      aria-label="Persisted move information"
    >
      <Popover
        direction="bottom"
        width="320px"
        text={tooltip}
        ariaLabel={ACCESSIBLE_LABEL}
      >
        <PersistedMoveIndicator
          testId={testId}
          onMouseEnter={triggerLoad}
          onFocus={triggerLoad}
          onClick={triggerLoad}
        />
      </Popover>
    </FlexItem>
  );
};

PersistedMoveParentInfo.propTypes = {
  group: PropTypes.object.isRequired,
  testId: PropTypes.string,
};

export default PersistedMoveParentInfo;
